import BillingModel from "../../../types/billing-model";
import BillingFormData from "../types/billing-form.data";
import BillingsNode from "../../../common/types/billings-node";
import googleMapsRouteService from "../../../../../common/utils/google-maps-route/google-maps-route.service";
import BillingsOrderRailyTaxiDraft, {
  BillingsOrderRailyTaxiDraftBillingNode,
  BillingsOrderRailyTaxiDraftBonusType,
  BillingsOrderRailyTaxiDraftContract,
  BillingsOrderRailyTaxiDraftPenaltyType,
} from "../../../../../common/services/billings/order/raily-taxi/draft/billings-order-raily-taxi-draft";
import ContractDetails from "../types/contract-details";
import PenaltyType from "../types/penalty-type";
import BonusType from "../types/bonus-type";
import BillingsOrderRailyTaxiRecalculate, {
  BillingsOrderRailyTaxiRecalculateBillingNode,
  BillingsOrderRailyTaxiRecalculateBonusType,
  BillingsOrderRailyTaxiRecalculateContract,
  BillingsOrderRailyTaxiRecalculatePenaltyType,
} from "../../../../../common/services/billings/order/raily-taxi/recalculate/billings-order-raily-taxi-recalculate";

const createBillingNode = (
  node:
    | BillingsOrderRailyTaxiDraftBillingNode
    | BillingsOrderRailyTaxiRecalculateBillingNode,
  index: number,
  nodes:
    | BillingsOrderRailyTaxiDraftBillingNode[]
    | BillingsOrderRailyTaxiRecalculateBillingNode[],
) => {
  const previousItem = index - 1 >= 0 ? nodes[index - 1] : null;

  const url =
    previousItem !== null
      ? googleMapsRouteService.createGoogleMapsRouteUrl([
          { latitude: previousItem.lat, longitude: previousItem.lon },
          { latitude: node.lat, longitude: node.lon },
        ])
      : "";

  const result: BillingsNode = {
    id: node.uuid,
    nodeId: node.nodeUuid,
    lat: node.lat,
    lon: node.lon,
    description: node.description,
    checkoutDate: node.checkoutDate ?? null,
    checkoutEventUuid: node.checkoutEventUuid ?? null,
    plannedDate: node.plannedDate ?? null,
    plannedDistance: node.plannedDistance,
    orderId: node.orderUuid ?? null,
    distance: node.distance,
    haltingTime: node.haltingTime,
    highwayCharge: node.highwayCharge,
    isHighwayAllowed: node.allowCharge,
    isEditable: node.editableCoordinates,
    googleMapsUrl: url,
    position: index + 1,
  };

  return result;
};

const createBillingNodes = (
  nodes:
    | BillingsOrderRailyTaxiDraftBillingNode[]
    | BillingsOrderRailyTaxiRecalculateBillingNode[],
): BillingsNode[] => {
  return nodes.map(createBillingNode);
};

const createContract = (
  contract:
    | BillingsOrderRailyTaxiDraftContract
    | BillingsOrderRailyTaxiRecalculateContract,
): ContractDetails => {
  return {
    companyName: contract.taxiCorporation.companyName,
    haltingTimeRate: contract.haltingTimeRate,
    distanceRate: contract.distanceRate,
    model: contract.model,
    haltingTimeAppliedAfter: contract.haltingTimeAfterMinutes,
  };
};

const createPenaltyType = (
  penaltyType:
    | BillingsOrderRailyTaxiDraftPenaltyType
    | BillingsOrderRailyTaxiRecalculatePenaltyType
    | undefined,
): PenaltyType | undefined => {
  switch (penaltyType) {
    case BillingsOrderRailyTaxiDraftPenaltyType.BEING_LATE:
    case BillingsOrderRailyTaxiRecalculatePenaltyType.BEING_LATE:
      return PenaltyType.BEING_LATE;
    case BillingsOrderRailyTaxiDraftPenaltyType.GUIDELINE_IGNORED:
    case BillingsOrderRailyTaxiRecalculatePenaltyType.GUIDELINE_IGNORED:
      return PenaltyType.GUIDELINE_IGNORED;
    case BillingsOrderRailyTaxiDraftPenaltyType.HIGHWAY_NOT_ALLOWED:
    case BillingsOrderRailyTaxiRecalculatePenaltyType.HIGHWAY_NOT_ALLOWED:
      return PenaltyType.HIGHWAY_NOT_ALLOWED;
    case BillingsOrderRailyTaxiDraftPenaltyType.INCOMPATIBLE_CAR:
    case BillingsOrderRailyTaxiRecalculatePenaltyType.INCOMPATIBLE_CAR:
      return PenaltyType.INCOMPATIBLE_CAR;
    case BillingsOrderRailyTaxiDraftPenaltyType.OPTIMIZATION_REFUSED:
    case BillingsOrderRailyTaxiRecalculatePenaltyType.OPTIMIZATION_REFUSED:
      return PenaltyType.OPTIMIZATION_REFUSED;
    case BillingsOrderRailyTaxiDraftPenaltyType.OTHER:
    case BillingsOrderRailyTaxiRecalculatePenaltyType.OTHER:
      return PenaltyType.OTHER;
    case BillingsOrderRailyTaxiDraftPenaltyType.SUBOPTIMAL_RIDE:
    case BillingsOrderRailyTaxiRecalculatePenaltyType.SUBOPTIMAL_RIDE:
      return PenaltyType.SUBOPTIMAL_RIDE;
    default:
      return undefined;
  }
};

const createBonusType = (
  bonusType:
    | BillingsOrderRailyTaxiDraftBonusType
    | BillingsOrderRailyTaxiRecalculateBonusType
    | undefined,
): BonusType | undefined => {
  switch (bonusType) {
    case BillingsOrderRailyTaxiDraftBonusType.DISTANCE_RATE_CHANGED:
    case BillingsOrderRailyTaxiRecalculateBonusType.DISTANCE_RATE_CHANGED:
      return BonusType.DISTANCE_RATE_CHANGED;
    case BillingsOrderRailyTaxiDraftBonusType.HALTING_RATE_CHANGED:
    case BillingsOrderRailyTaxiRecalculateBonusType.HALTING_RATE_CHANGED:
      return BonusType.HALTING_RATE_CHANGED;
    case BillingsOrderRailyTaxiDraftBonusType.OTHER:
    case BillingsOrderRailyTaxiRecalculateBonusType.OTHER:
      return BonusType.OTHER;
    case BillingsOrderRailyTaxiDraftBonusType.SHORT_ROUTE:
    case BillingsOrderRailyTaxiRecalculateBonusType.SHORT_ROUTE:
      return BonusType.SHORT_ROUTE;
    default:
      undefined;
  }
};

const createBillingData = (
  data: BillingsOrderRailyTaxiDraft | BillingsOrderRailyTaxiRecalculate,
) => {
  const billingDraft: BillingFormData = {
    billingNodes: createBillingNodes(data.billingNodes),
    contractDetails: createContract(data.contract),
    haltingCost: data.haltingAmount,
    highwayCharge: data.highwayCharge,
    distance: data.distance,
    routeDistanceCost: data.distanceAmount,
    internalOrderId: data.cargoOrder.humanId,
    total: data.totalAmount,
    planEntryId: data.planEntryUuid,
    bonus: data.bonus,
    penalty: data.penalty,
    penaltyType: createPenaltyType(data.penaltyType),
    bonusType: createBonusType(data.bonusType),
    date: data.date,
  };

  return billingDraft;
};

const billingDataFactory = {
  createBillingData,
};

export default billingDataFactory;
